import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  useSignals,
  useSignalsByIds,
  useDeleteSignal,
  useActivateSignal,
  useDeactivateSignal,
  useRemoveImportedSignal,
} from '@/shared/hooks/useSignals';
import {
  useEmployees,
  useManagerDirectEmployees,
  useManagerEmployees,
} from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import type { Signal } from '@momentum/types';
import { Role } from '@momentum/types';
import { DEFAULT_SIGNAL_FILTERS, type SignalFilters } from './constants';
import { KnowledgePageHeader } from './components/KnowledgePageHeader';
import { SignalFilterBar } from './filters/SignalFilterBar';
import { SignalContent } from './signal-list/SignalContent';
import { SignalDetailModal } from './components/SignalDetailModal';
import { SignalLoadingState } from './signal-list/components/SignalLoadingState';
import { SignalErrorState } from './signal-list/components/SignalErrorState';
import { DeleteSignalDialog } from './components/DeleteSignalDialog';
import { CreateSignalDialog } from './components/CreateSignalDialog';
import { ActivateScopeDialog } from './components/ActivateScopeDialog';
import { DeactivateConfirmDialog } from './components/DeactivateConfirmDialog';

export const KnowledgePage = () => {
  const { t } = useTranslation('knowledge');
  const currentUser = useEmployeeContext();
  const search = useSearch({ from: '/knowledge' });
  const signalIdsParam = search.signalIds;
  const signalIds = signalIdsParam
    ? signalIdsParam
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean)
    : undefined;
  const isFilteredView = !!signalIds && signalIds.length > 0;

  const isManager =
    currentUser.role === Role.manager ||
    currentUser.role === Role.admin ||
    currentUser.role === Role.system_admin;

  // Filters
  const [filters, setFilters] = useState<SignalFilters>(DEFAULT_SIGNAL_FILTERS);

  // Modal state
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [signalToDelete, setSignalToDelete] = useState<Signal | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Activate/Deactivate dialog state
  const [activateScopeSignal, setActivateScopeSignal] = useState<Signal | null>(
    null
  );
  const [deactivateSignal, setDeactivateSignal] = useState<Signal | null>(null);

  // Data fetching
  const allSignalsQuery = useSignals(undefined, {
    enabled: !isFilteredView,
  });
  const signalsByIdsQuery = useSignalsByIds(signalIds ?? [], {
    enabled: isFilteredView,
  });

  const {
    data: allSignals = [],
    isLoading,
    error,
    refetch,
  } = isFilteredView ? signalsByIdsQuery : allSignalsQuery;

  const selectedSignal =
    allSignals.find((signal) => signal._id === selectedSignalId) ?? null;

  const { data: employees = [] } = useEmployees({ isActive: true });

  const employeeMap = useMemo(
    () =>
      new Map(
        employees.map((emp) => [
          emp._id,
          {
            fullName: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
            imageUrl: emp.personalInfo.imageUrl,
            role: emp.role,
          },
        ])
      ),
    [employees]
  );

  // Subordinate counts for activate scope dialog
  const managerParams = {
    managerId: isManager ? currentUser._id : '',
    excludeManager: true,
  };
  const { data: directReports = [] } = useManagerDirectEmployees(managerParams);
  const { data: allSubordinates = [] } = useManagerEmployees(managerParams);

  const owners = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ id: string; fullName: string; imageUrl?: string }> =
      [];
    for (const signal of allSignals) {
      if (!seen.has(signal.createdBy)) {
        seen.add(signal.createdBy);
        const emp = employeeMap.get(signal.createdBy);
        if (emp) {
          result.push({
            id: signal.createdBy,
            fullName: emp.fullName,
            imageUrl: emp.imageUrl,
          });
        }
      }
    }
    return result;
  }, [allSignals, employeeMap]);

  // Mutations
  const deleteSignalMutation = useDeleteSignal();
  const activateMutation = useActivateSignal();
  const deactivateMutation = useDeactivateSignal();
  const removeMutation = useRemoveImportedSignal();

  const togglingSignalId = activateMutation.isPending
    ? (activateMutation.variables as { id: string })?.id
    : deactivateMutation.isPending
    ? (deactivateMutation.variables as string)
    : null;

  const handleToggleActive = async (signal: Signal, isActive: boolean) => {
    if (isActive) {
      // Deactivating — check if manager has applied users
      if (isManager) {
        const appliedCount =
          signal.installed?.users?.filter(
            (installedUser) =>
              installedUser.appliedBy?.toString() === currentUser._id
          ).length ?? 0;

        if (appliedCount > 0) {
          setDeactivateSignal(signal);
          return;
        }
      }

      // No applied users or not a manager — deactivate directly
      try {
        await deactivateMutation.mutateAsync(signal._id);
      } catch {
        toast.error(t('deactivateSignalError'));
      }
    } else {
      // Activating — manager gets scope dialog
      if (isManager) {
        setActivateScopeSignal(signal);
        return;
      }

      // Non-manager — activate directly
      try {
        await activateMutation.mutateAsync({ id: signal._id });
      } catch {
        toast.error(t('activateSignalError'));
      }
    }
  };

  const handleRemove = (signal: Signal) => {
    removeMutation.mutate(signal._id, {
      onSuccess: () => {
        setIsDrawerOpen(false);
        setSelectedSignalId(null);
      },
      onError: () => toast.error(t('marketplace.removeError')),
    });
  };

  const handleSignalClick = (signal: Signal) => {
    setSelectedSignalId(signal._id);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (signal: Signal) => {
    setSignalToDelete(signal);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!signalToDelete) return;

    try {
      await deleteSignalMutation.mutateAsync(signalToDelete._id);

      if (selectedSignalId === signalToDelete._id) {
        setIsDrawerOpen(false);
        setSelectedSignalId(null);
      }
    } catch {
      toast.error(t('deleteSignalError'));
    } finally {
      setIsDeleteDialogOpen(false);
      setSignalToDelete(null);
    }
  };

  const handleAddSignal = () => {
    setIsCreateDialogOpen(true);
  };

  return (
    <div className="relative min-h-screen bg-page-bg">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[350px] bg-gradient-to-b from-[#ddd6f3] via-[#ebe7f6] to-[var(--page-bg)]" />

      <KnowledgePageHeader onAddSignal={handleAddSignal} />

      <div className="relative mx-4 min-h-[calc(100vh-120px)] py-6 sm:mx-6 lg:mx-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-10">
          <SignalFilterBar
            filters={filters}
            onFiltersChange={setFilters}
            owners={owners}
          />

          {isLoading && <SignalLoadingState />}

          {error && (
            <SignalErrorState error={error} onRetry={() => refetch()} />
          )}

          {!error && !isLoading && (
            <SignalContent
              allSignals={allSignals}
              filters={filters}
              isFilteredView={isFilteredView}
              currentUserId={currentUser._id}
              employeeMap={employeeMap}
              onSignalClick={handleSignalClick}
              onToggleActive={handleToggleActive}
              togglingSignalId={togglingSignalId}
              onAddSignal={handleAddSignal}
            />
          )}

          <SignalDetailModal
            signal={selectedSignal}
            currentUserId={currentUser._id}
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onDelete={handleDeleteClick}
            onToggleActive={handleToggleActive}
            isTogglingActive={
              activateMutation.isPending || deactivateMutation.isPending
            }
            onRemove={handleRemove}
            employeeMap={employeeMap}
            isManager={isManager}
            directReportsCount={directReports.length}
            fullHierarchyCount={allSubordinates.length}
          />

          <DeleteSignalDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onConfirm={handleDeleteConfirm}
            signal={signalToDelete}
            isDeleting={deleteSignalMutation.isPending}
          />

          <CreateSignalDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          />

          <ActivateScopeDialog
            signal={activateScopeSignal}
            onClose={() => setActivateScopeSignal(null)}
            directReportsCount={directReports.length}
            fullHierarchyCount={allSubordinates.length}
          />

          <DeactivateConfirmDialog
            signal={deactivateSignal}
            onClose={() => setDeactivateSignal(null)}
            currentUserId={currentUser._id}
          />
        </div>
      </div>
    </div>
  );
};
